/**
 * AdvisorConsentDialog — §3-Mandanten-Consent-Dialog (B3,
 * architecture-stb-onboarding-consent.md §5.2/§6.4).
 *
 * Modaler Dialog, der VOR jeder consent-pflichtigen Advisor-Einladungs-Aktion
 * gezeigt wird (Richtung a = Owner→StB beim Absenden; Richtung b = Kanzlei→Mandant
 * beim Annehmen). Zeigt den versionierten §3-Text (operator-verfasst, per Fetch
 * geladen via {@link useLegalDocument}), interpoliert die einzige Variable
 * `{{kanzlei_name}}`, und verlangt eine UNVORBELEGTE Einwilligungs-Checkbox
 * (Kopplungsverbot). Der „Zugriff gewähren"-Button ist gesperrt, solange die
 * Checkbox leer ist ODER das Dokument fehlt/im Entwurf ist (Draft-Marker-Sperre =
 * Fail-safe: ohne freigegebene Datei bleibt der Consent-Zwang inert).
 *
 * Präsentational: der Dialog lädt zwar das Dokument selbst, führt aber KEINE
 * Callable aus — bei Bestätigung ruft er `onGrant()`, und die einbettende
 * Komponente sendet die eigentliche Einladung/Annahme mit `consentAccepted:true`.
 * `submitting`/`error` spiegeln den Zustand dieses nachgelagerten Aufrufs.
 *
 * Die §3-Datei (`public/legal/advisor-consent-v1.md`) legt der Operator an (J-3,
 * M-OB-15) — dieser Dialog LIEST sie nur.
 *
 * @module components/tenant/AdvisorConsentDialog
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconWarning } from '../../icons';
import SimpleMarkdown from '../inbound/SimpleMarkdown';
import {
  ADVISOR_CONSENT_DOC_PATH,
  LEGAL_DRAFT_MARKER,
} from '../../config/advisorConsentAccess';
import { useLegalDocument, interpolatePlaceholder } from './useLegalDocument';

interface AdvisorConsentDialogProps {
  /**
   * Name/Bezeichnung der Kanzlei für die `{{kanzlei_name}}`-Interpolation.
   * Richtung (a): die vom Mandanten eingegebene Empfänger-E-Mail; Richtung (b):
   * `kanzleiName` aus `previewClientInvite`. Null ⇒ generischer Fallback.
   */
  kanzleiName: string | null;
  /** True, solange die nachgelagerte Einladung/Annahme läuft (Buttons gesperrt). */
  submitting?: boolean;
  /** Optionaler Fehlertext des nachgelagerten Aufrufs. */
  error?: string | null;
  /** Aufgerufen, wenn der Mandant die Einwilligung erteilt (Checkbox + Bestätigen). */
  onGrant: () => void;
  /** Aufgerufen bei Abbruch/Schließen. */
  onCancel: () => void;
}

/**
 * §3-Consent-Modal. Fail-safe: gesperrt, solange die Legal-Datei nicht `ready` ist.
 */
const AdvisorConsentDialog: React.FC<AdvisorConsentDialogProps> = ({
  kanzleiName,
  submitting = false,
  error = null,
  onGrant,
  onCancel,
}) => {
  const { t, i18n } = useTranslation('common');
  const [accepted, setAccepted] = useState<boolean>(false); // UNVORBELEGT (Kopplungsverbot)
  const docState = useLegalDocument(ADVISOR_CONSENT_DOC_PATH, LEGAL_DRAFT_MARKER);

  const docReady = docState.kind === 'ready';
  const docBlocked = docState.kind === 'missing' || docState.kind === 'draft';
  const canGrant = docReady && accepted && !submitting;
  const isGerman = i18n.language?.toLowerCase().startsWith('de') ?? false;

  const renderedText =
    docState.kind === 'ready' || docState.kind === 'draft'
      ? interpolatePlaceholder(
          docState.text,
          'kanzlei_name',
          kanzleiName,
          t('advisorConsent.kanzleiNameFallback'),
        )
      : '';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="advisor-consent-title"
      data-testid="advisor-consent-dialog"
    >
      <div className="w-full max-w-lg rounded-xl border border-hairline bg-surface-raised p-6 shadow-2xl">
        <h2
          id="advisor-consent-title"
          className="text-lg font-semibold text-primary"
        >
          {t('advisorConsent.title')}
        </h2>
        <p className="mt-1 text-sm text-muted">{t('advisorConsent.intro')}</p>

        {/* Rechtstext ist DE-verbindlich (§9): in Nicht-DE-Locales ein Hinweis. */}
        {!isGerman && docReady && (
          <p className="mt-3 rounded-md bg-surface-inset px-3 py-2 text-xs text-secondary">
            {t('advisorConsent.deOnlyNotice')}
          </p>
        )}

        {/* §3-Dokument — fail-closed bei fehlend/Entwurf. */}
        <div
          className="mt-3 max-h-56 overflow-y-auto rounded-md border border-hairline bg-[rgb(var(--surface-base-rgb)/0.6)] p-3 text-xs text-secondary"
          data-testid="advisor-consent-doc"
        >
          {docReady || docState.kind === 'draft' ? (
            <SimpleMarkdown text={renderedText} />
          ) : docState.kind === 'loading' ? (
            t('advisorConsent.loading')
          ) : (
            t('advisorConsent.notReleased')
          )}
        </div>

        {docBlocked && (
          <div
            className="mt-3 flex items-start gap-2 rounded-md bg-[var(--tone-warning-bg)] border border-[var(--tone-warning-border)] p-3"
            role="status"
            data-testid="advisor-consent-blocked"
          >
            <IconWarning className="mt-0.5 text-[var(--tone-warning-fg)]" />
            <p className="text-sm text-[var(--tone-warning-fg)]">
              {docState.kind === 'draft'
                ? t('advisorConsent.draft')
                : t('advisorConsent.notReleased')}
            </p>
          </div>
        )}

        {/* UNVORBELEGTE Einwilligungs-Checkbox (Kopplungsverbot). */}
        <label className="mt-4 flex items-start gap-2 text-sm text-secondary">
          <input
            type="checkbox"
            checked={accepted}
            disabled={!docReady || submitting}
            onChange={(e) => setAccepted(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-strong bg-surface text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
            data-testid="advisor-consent-checkbox"
          />
          <span>
            {interpolatePlaceholder(
              t('advisorConsent.checkboxLabel'),
              'kanzlei_name',
              kanzleiName,
              t('advisorConsent.kanzleiNameFallback'),
            )}
          </span>
        </label>

        {error && (
          <p
            className="mt-3 text-sm text-[var(--tone-danger-fg)]"
            data-testid="advisor-consent-error"
          >
            {error}
          </p>
        )}

        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="inline-flex items-center rounded-md bg-surface-inset px-3 py-1.5 text-sm font-medium text-secondary hover:bg-surface-hover disabled:opacity-50"
            data-testid="advisor-consent-cancel"
          >
            {t('advisorConsent.cancel')}
          </button>
          <button
            type="button"
            onClick={onGrant}
            disabled={!canGrant}
            className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="advisor-consent-grant"
          >
            {submitting ? t('advisorConsent.granting') : t('advisorConsent.grantButton')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdvisorConsentDialog;
